import { IPublicClientApplication } from '@azure/msal-browser';
import { EmailTemplateAttachment, EmailTemplateRequest } from '../../models';
import { EmailResourceType } from '../../models/resource-services';
import { ApiEndpointCallStatesStore } from '../api-endpoint-call-states-store';
import { FetchContext } from '../fetch-context';
import { promiseWithObservableState } from '../promise-with-observable-state';
import { withApiAccessToken } from '../with-api-access-token';
import {
  createEmailResourcesDownloadTicket,
  createEmailTemplate,
  deleteEmailTemplate,
  getEmailTemplate,
  getEmailTemplateRefs,
  getSentEmails,
  sendEmailTemplate,
  updateEmailTemplate,
  uploadEmailTemplateAttachment,
  uploadEmailTemplateBannerImage,
  upsertEmailTemplateAttachments,
} from './index';

export const createEmailTemplatesApiEndpoints = (
  fetchContext: FetchContext,
  apiEndpointCallStates: ApiEndpointCallStatesStore,
) => ({
  createEmailTemplate: (instance: IPublicClientApplication, programId: number, emailTemplate: EmailTemplateRequest, isForSending: boolean, isTemporary: boolean) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createEmailTemplate(fetchContext, programId, emailTemplate, isForSending, isTemporary, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createEmailTemplate', isInFlight);
    },
  ),
  createEmailResourcesDownloadTicket: (instance: IPublicClientApplication, resourceType: EmailResourceType) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createEmailResourcesDownloadTicket(fetchContext, resourceType, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createEmailResourcesDownloadTicket', isInFlight);
    },
  ),
  deleteEmailTemplate: (instance: IPublicClientApplication, programId: number, emailTemplateId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteEmailTemplate(fetchContext, programId, emailTemplateId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteEmailTemplate', isInFlight);
    },
  ),
  getEmailTemplateRefs: (instance: IPublicClientApplication, programId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getEmailTemplateRefs(fetchContext, programId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getEmailTemplateRefs', isInFlight);
    },
  ),
  getEmailTemplate: (instance: IPublicClientApplication, programId: number, emailTemplateId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getEmailTemplate(fetchContext, programId, emailTemplateId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getEmailTemplate', isInFlight);
    },
  ),
  getSentEmails: (instance: IPublicClientApplication, programId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getSentEmails(fetchContext, programId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getSentEmails', isInFlight);
    },
  ),
  sendEmailTemplate: (instance: IPublicClientApplication, programId: number, emailTemplateId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => sendEmailTemplate(fetchContext, programId, emailTemplateId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('sendEmailTemplate', isInFlight);
    },
  ),
  updateEmailTemplate: (instance: IPublicClientApplication, programId: number, emailTemplateId: number, emailTemplate: EmailTemplateRequest, isForSending: boolean) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateEmailTemplate(fetchContext, programId, emailTemplateId, emailTemplate, isForSending, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateEmailTemplate', isInFlight);
    },
  ),
  uploadEmailTemplateAttachment: (instance: IPublicClientApplication, programId: number, emailTemplateId: number, attachmentFile: File) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => uploadEmailTemplateAttachment(fetchContext, programId, emailTemplateId, attachmentFile, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('uploadEmailTemplateAttachment', isInFlight);
    },
  ),
  uploadEmailTemplateBannerImage: (instance: IPublicClientApplication, programId: number, attachmentFile: File) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => uploadEmailTemplateBannerImage(fetchContext, programId, attachmentFile, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('uploadEmailTemplateBannerImage', isInFlight);
    },
  ),
  upsertEmailTemplateAttachments: (instance: IPublicClientApplication, programId: number, emailTemplateId: number, emailTemplateAttachments: EmailTemplateAttachment[]) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => upsertEmailTemplateAttachments(fetchContext, programId, emailTemplateId, emailTemplateAttachments, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('upsertEmailTemplateAttachments', isInFlight);
    },
  ),
});
